import { Col, Row } from "react-bootstrap";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useLoader } from "../../context/LoaderContext";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FileLocationModel } from "../../types/FileLocationModel";
import {
  DeleteFileLocationById,
  DownloadExcel,
  GetFileLocationData,
} from "../../api/fileLocationApi/fileLocationApi";
import { Button, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useNavigate } from "react-router-dom";
import FileUploads from "../Alert/FileUploads";
import { useSnackbar } from "../Common/SnackbarContext";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { appSettings } from "../../config/appSetting";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";
import { DialogProps } from "../../types/SimpleSearchModel";
import ConfirmationDialog from "../SimpleSearch/ConfirmationDialog";

const FileLocation: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const { setLoading } = useLoader();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const navigation = useNavigate();
  const [uploadFilesOpen, setUploadFilesOpen] = useState(false);

  //Fetch Data on Page Load
  useEffect(() => {
    fetchData();
  }, []);

  // Define columns to exclude
  const columnsToExclude = [
    "ftpFolderId",
    "location",
    "userName",
    "password",
    "accountId",
    "enableBackup",
    "createdBy",
    "active",
    "orderBy",
    "protocol",
    "createdDate",
    "lastModifiedDate",
    "backUpPath",
    "searchText",
    "superAdmin",
    "listEmailTemplateModel",
    "recordCount",
    "parentFTPFolderId",
    "noOfRecords",
    "currentPage",
    "sortColumn",
    "sortOrder",
    "totalRecords",
    "isexporttpExcel",
    "contextName",
  ];

  //Rename the columns
  const renameColumns = (val: any) => {
    if (val !== undefined) {
      if (val.toLowerCase() === "foldername") {
        return "Display Name";
      } else if (val.toLowerCase() === "accountname") {
        return "Customer";
      } else if (val.toLowerCase() === "filelocationtype") {
        return "Location Type";
      } else if (val.toLowerCase() === "ftppath") {
        return "Location / Path";
      } else if (val.toLowerCase() === "servername") {
        return "Server";
      } else if (val.toLowerCase() === "port") {
        return "Port";
      } else {
        return val.replace(/&amp;/, "&");
      }
    }
  };

  //Get File Location Data
  const fetchData = async () => {
    setLoading(true);
    try {
      const parameter: FileLocationModel = {
        FTPFolderId: 0,
        BusinessUnitId: 0,
        FolderName: "",
        AccountName: "",
        FileLocationType: "",
        Location: "",
        ServerName: "",
        Port: 0,
        UserName: "",
        Password: "",
        AccountId: Number(LocalStorageUtil.getItem("accountId")),
        EnableBackup: false,
        CreatedBy: 0,
        Active: 0,
        OrderBy: 0,
        Protocol: 0,
        LastModifiedDate: new Date(),
        CreatedDate: new Date(),
        BackUpPath: "",
        SearchText: "",
        FileFormat: "",
        SuperAdmin: 1,
        ListEmailTemplateModel: [],
        RecordCount: 0,
        ParentFTPFolderId: 0,
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        CurrentPage: 0,
        NoOfRecords: 0,
      };
      const response = await GetFileLocationData(parameter);
      if (response && response.listEmailTemplateModel.length > 0) {
        setRowData(response.listEmailTemplateModel);

        //Set Column Defs dynamically
        const dynamicColumns = Object.keys(response.listEmailTemplateModel[0])
          .filter((key) => !columnsToExclude.includes(key))
          .map((key: string) => {
            if (key === "fileLocationType") {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                flex: 1,
                valueGetter: (params: any) => {
                  if (
                    params.data !== undefined &&
                    params.data.fileLocationType === 2 &&
                    params.data.protocol === 1
                  ) {
                    return "FTP Location";
                  } else if (
                    params.data !== undefined &&
                    params.data.fileLocationType === 2 &&
                    params.data.protocol === 2
                  ) {
                    return "SFTP Location";
                  } else if (
                    params.data !== undefined &&
                    params.data.fileLocationType === 1
                  ) {
                    return "Network Folder";
                  }
                },
              };
            } else {
              return {
                headerName: renameColumns(
                  key.charAt(0).toUpperCase() + key.slice(1)
                ),
                field: key,
                sortable: true,
                filter: true,
                enableRowGroup: true,
                flex: 1,
              };
            }
          });

        const combinedColumnDefs = [
          {
            headerName: "#",
            width: 70,
            suppressSizeToFit: true,
            colId: "colRowNum",
            sortable: false,
            filter: false,
            resizable: false,
            enableRowGroup: false,
            valueGetter: (params: any) => {
              return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
            },
          },
          ...dynamicColumns,
          {
            field: "ftpFolderId",
            headerName: "Actions",
            sortable: false,
            enableRowGroup: false,
            pinned: "right" as const,
            lockPinned: true,
            suppressSizeToFit: true,
            width: 100,
            minWidth: 100,
            cellClass: "actions-button-cell",
            cellRenderer: actionCellRenderer,
            filter: false,
            resizable: false,
            colId: "colAction",
          },
        ];
        setColumnDefs(combinedColumnDefs);
      } else {
        setRowData([]);
      }
    } catch (error) {
      showSnackbar("Error in File Location Data fetching", true);
    } finally {
      setLoading(false);
    }
  };

  //Quick Filter
  const onFilterTextBoxChanged = useCallback(() => {
    gridRef.current!.api.setGridOption(
      "quickFilterText",
      (document.getElementById("filter-text-box") as HTMLInputElement).value
    );
  }, []);

  //Handel Edit action
  const editAction = (rowData: any) => {
    const selectedRowData = rowData;
    navigation("/createNew", {
      state: { rowData: selectedRowData, action: "Edit" },
    });
  };

  // #region Top Action Buttons
  const [dialogProps, setDialogProps] = useState<DialogProps>({
    dialogTitle: "",
    contentText: "",
    confirmText: "",
    closeText: "",
    onConfirm: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleDelete = (selectedRows: any) => {
    setDialogProps({
      dialogTitle: "Delete File Location",
      contentText:
        "Are you sure you want to delete this record?",
      confirmText: "Yes",
      closeText: "No",
      onConfirm: () => handleDeleteDocument(selectedRows),
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleDeleteDocument = async (rowData: any) => {
    setLoading(true);
    setDialogOpen(false);
      const params: FileLocationModel = {
        FTPFolderId: rowData.ftpFolderId,
        BusinessUnitId: Number(LocalStorageUtil.getItem("businessUnitId")),
        FolderName: undefined,
        AccountName: undefined,
        FileLocationType: undefined,
        Location: undefined,
        ServerName: undefined,
        Port: undefined,
        UserName: undefined,
        Password: undefined,
        AccountId: Number(LocalStorageUtil.getItem("accountId")),
        EnableBackup: false,
        CreatedBy: undefined,
        Active: 1,
        OrderBy: undefined,
        Protocol: undefined,
        LastModifiedDate: undefined,
        CreatedDate: undefined,
        BackUpPath: undefined,
        SearchText: undefined,
        FileFormat: undefined,
        SuperAdmin: undefined,
        ListEmailTemplateModel: undefined,
        RecordCount: undefined,
        ParentFTPFolderId: undefined,
        ContextName: LocalStorageUtil.getItem("contextName") ?? "",
        CurrentPage: undefined,
        NoOfRecords: undefined,
      };
      try {
        const data = await DeleteFileLocationById(params);
        showSnackbar("File Location deleted successfully");
        fetchData();
      } catch (error) {
        showSnackbar("File Location associated with subsystem.", true);
      } finally {
        setLoading(false);
      }
    };

  //Action Cell Renderer
  const actionCellRenderer = (params: any) => {
    if (params.data === undefined) {
      return "";
    } else {
      return (
        <>
          {isPagePermission("Allow Edit", "Filelocation", 403) && (
            <Tooltip title="Click to Edit the location">
              <a href="#" className="icon-button">
                <EditIcon onClick={() => editAction(params.data)} />
              </a>
            </Tooltip>
          )}
          {isPagePermission("Allow Delete", "Filelocation", 1463) && (
            <Tooltip title="Delete this location">
              <a href="#" className="icon-button" style={{ marginLeft: 8 }}>
                <DeleteIcon onClick={() => handleDelete(params.data)} />
              </a>
            </Tooltip>
          )}
        </>
      );
    }
  };

  //Donwload File Function
  const downloadFile = async (fileType: any) => {
    setLoading(true);
    try {
      const params: any = {
        IsSuperAdmin: 1,
        AccountId: LocalStorageUtil.getItem("accountId"),
        isexporttpExcel: true,
        pagename: "FileLocation",
        aliasname: "FileLocation",
        businessUnitId: LocalStorageUtil.getItem("businessUnitId")
          ? Number(LocalStorageUtil.getItem("businessUnitId"))
          : 0,
        contextName: LocalStorageUtil.getItem("contextName")?.toString(),
        fTPLocationType: fileType,
        offset: "Asia/Calcutta",
      };
      const response = await DownloadExcel(params);
      if (response && response.status === 200 && response.data) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const filename =
          "FileLocation_" + new Date().toISOString().split("T")[0] + ".xlsx";
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSnackbar("File downloaded successfully");
      } else {
        showSnackbar("Unable to download file !", true);
      }
    } catch (e) {
      showSnackbar("Error in File Location template downloading", true);
    } finally {
      setLoading(false);
    }
  };

  //Download SFTP/FTP Template
  const downloadFTPTemplate = () => {
    downloadFile(1);
    showSnackbar("File downloaded successfully");
  };

  //Download UNC Template
  const downloadUNC = () => {
    downloadFile(2);
    showSnackbar("File downloaded successfully");
  };

  const handelCreateNew = () => {
    navigation("/createNew");
  };

  const handelUploadFiles = () => {
    setUploadFilesOpen(true);
  };

  const closeUploadFiles = (isCancelClick: boolean) => {
    setUploadFilesOpen(false);
    if (!isCancelClick) {
      fetchData();
    }
  };

  //Rendering File Location Component
  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar
        title="File Location"
        text="File Location"
        menuTitle="Configuration"
      />
      <Row style={{ marginLeft: "1em" }}>
        <Col xs={12}>
          <div
            className="rp-main-inner col-sm-12"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            {/* Quick Filter section */}

            <div
              className="col-sm-5"
              style={{
                width: "48%",
                padding: ".5em",
                marginTop: ".1em",
              }}
            >
              <input
                type="text"
                id="filter-text-box"
                placeholder="Quick Filter..."
                style={{
                  width: "25em",
                  height: "2.5em",
                  fontSize: "11px",
                  paddingLeft: ".5em",
                  marginBottom: ".1em",
                }}
                onInput={onFilterTextBoxChanged}
              />
            </div>
            <div
              className="col-sm-5"
              style={{
                width: "50%",
                textAlign: "right",
                margin: ".1em",
              }}
            >
              {isPagePermission(
                "Allow Download Template",
                "Filelocation",
                1374
              ) && (
                <Button
                  variant="contained"
                  size="small"
                  className="button download-template-button"
                  onClick={downloadFTPTemplate}
                >
                  FTP/SFTP Download Template
                </Button>
              )}
              {isPagePermission(
                "Allow Download Template",
                "Filelocation",
                1374
              ) && (
                <Button
                  variant="contained"
                  size="small"
                  className="button metadata-button"
                  onClick={downloadUNC}
                >
                  UNC Download Template
                </Button>
              )}
              {isPagePermission("Allow Create New", "Filelocation", 402) && (
                <Button
                  variant="contained"
                  size="small"
                  className="button createnew-button"
                  onClick={handelCreateNew}
                >
                  Create new
                </Button>
              )}
              {isPagePermission(
                "Allow Upload Template",
                "Filelocation",
                1375
              ) && (
                <Button
                  variant="contained"
                  size="small"
                  className="button upload-button"
                  onClick={handelUploadFiles}
                >
                  Upload
                </Button>
              )}
            </div>
          </div>
          <div className="rp-main-inner col-sm-12">
            {/* Ag-Grid Section */}
            <div className="rp-main-inner col-sm-12">
              <div
                className="ag-theme-quartz-dark"
                style={{
                  height: "66vh",
                  marginTop: ".1vh",
                  width: "99%",
                }}
              >
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={rowData}
                  pagination={true}
                  paginationPageSize={defaulPageSize}
                  paginationPageSizeSelector={pageSizeSelector}
                  rowGroupPanelShow="always"
                  pivotPanelShow="always"
                  ref={gridRef}
                  headerHeight={30}
                  loading={!rowData}
                  overlayNoRowsTemplate={
                    '<span class="ag-overlay-loading-center">No rows to show</span>'
                  }
                  loadingOverlayComponent={CustomLoadingOverlay}
                />
              </div>
            </div>
          </div>
          <ConfirmationDialog
              open={dialogOpen}
              onClose={handleDialogClose}
              onConfirm={dialogProps.onConfirm}
              contentText={dialogProps.contentText}
              closeText={dialogProps.closeText}
              confirmText={dialogProps.confirmText}
              dialogTitle={dialogProps.dialogTitle}
            />
          {/* Upload Files Dialog */}
          {uploadFilesOpen && (
            <FileUploads
              isOpen={uploadFilesOpen}
              onClose={closeUploadFiles}
              title="Upload Files"
              selectedData={null}
              screenType="FileLocation"
            />
          )}
        </Col>
      </Row>
    </div>
  );
};
export default FileLocation;
